/**
 * ChatConnectionIndicator - inline connection status shown in the chat pane header.
 *
 * Visible only when the WebSocket is not connected. Renders nothing when
 * connected or idle (normal operating state).
 *
 * States:
 *   reconnecting (attempt 1–5) - shows "Reconnecting X/5…" with a pulse dot
 *   exhausted (attempt ≥ 5)   - shows a "Reconnect" button
 *   error                      - shows a "Reconnect" button (auth error or similar)
 */

import type { ChatConnectionStatus } from '../state/chatState';

export const MAX_RECONNECT_ATTEMPTS = 5;

export interface ChatConnectionIndicatorProps {
  connectionStatus: ChatConnectionStatus;
  reconnectAttempt: number;
  onReconnect: () => void;
  /** A run is in flight. The server keeps the run alive across a reconnect (subscribe_run replays),
   * so we reassure ("Still running - reconnecting display") instead of implying the work was lost. */
  isRunActive?: boolean;
}

export function ChatConnectionIndicator({
  connectionStatus,
  reconnectAttempt,
  onReconnect,
  isRunActive = false,
}: ChatConnectionIndicatorProps) {
  // Nothing to show when connected or idle (happy path).
  if (connectionStatus === 'connected' || connectionStatus === 'idle') return null;

  // Initial connect in progress - no attempt counter yet, don't distract.
  if (connectionStatus === 'connecting' && reconnectAttempt === 0) return null;

  const isRetrying =
    (connectionStatus === 'disconnected' || connectionStatus === 'connecting') &&
    reconnectAttempt > 0 &&
    reconnectAttempt <= MAX_RECONNECT_ATTEMPTS;

  const needsManual =
    (connectionStatus === 'disconnected' && reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) ||
    connectionStatus === 'error';

  if (needsManual) {
    return (
      <button
        type="button"
        className="chat-conn-indicator chat-conn-indicator--btn"
        onClick={onReconnect}
        aria-label="Reconnect to chat"
      >
        Reconnect
      </button>
    );
  }

  if (isRetrying) {
    // While a run is in flight the work is safe server-side (it survives the reconnect), so reassure
    // rather than alarm - the user was seeing "Reconnecting…" during a perfectly healthy long run.
    const label = isRunActive
      ? 'Still running - reconnecting the display…'
      : `Reconnecting ${reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS}…`;
    return (
      <span
        className="chat-conn-indicator chat-conn-indicator--retrying"
        aria-live="polite"
        aria-label={isRunActive ? 'Still running, reconnecting the display' : `Reconnecting, attempt ${reconnectAttempt} of ${MAX_RECONNECT_ATTEMPTS}`}
      >
        <span className="chat-conn-indicator__dot" aria-hidden="true" />
        {label}
      </span>
    );
  }

  return null;
}
